import { ref, watch, computed } from '@vue/composition-api';
import usePersistedPerPage from '@/libs/utils/perPage';
import { title } from '@core/utils/filter';
import moment from 'moment';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import store from '@/store';

export default function usePathways() {
  // Use toast
  const toast = useToast();

  const refPathwayListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'name', sortable: true },
    {
      key: 'courses',
      label: 'Courses',
      formatter: (value) => {
        if (value.length === 0) return false;
        return value;
      },
    },
    {
      key: 'live_cohorts',
      label: 'Live',
      formatter: (value, key, items) => {
        return items.sessions.filter((session) => session.status === 'live').length;
      },
    },
    {
      key: 'completed_cohorts',
      label: 'Completed',
      formatter: (value, key, items) => {
        return items.sessions.filter((session) => session.status === 'completed').length;
      },
    },
    // {
    //   key: 'createdAt',
    //   label: 'Created At',
    //   formatter: value => {
    //     return moment(value).format('L');
    //   },
    //   sortable: true,
    // },
    { key: 'visibility', sortable: true },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ];
  const perPage = usePersistedPerPage('pathways', 10);
  const totalPathways = ref(0);
  const totalPageCount = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('id');
  const isSortDirDesc = ref(true);

  const visibilityFilter = ref(null);
  const statusFilter = ref(null);

  const isLoaded = ref(false);

  const dataMeta = computed(() => {
    const localItemsCount = refPathwayListTable.value ? refPathwayListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPathways.value,
    };
  });

  const refetchData = () => {
    refPathwayListTable.value.refresh();
  };

  watch([currentPage, perPage, searchQuery, visibilityFilter, statusFilter], () => {
    refetchData();
  });

  const fetchPathwayList = (ctx, callback) => {
    const sbv = sortBy.value === 'id' ? '_id' : sortBy.value;
    const sb = `${sbv}:${isSortDirDesc.value ? 'desc' : 'asc'}`;

    const pl = {
      search: searchQuery.value,
      limit: perPage.value,
      page: currentPage.value,
      sortBy: sb,

      populate: 'sessions,courses',
    };

    if (visibilityFilter.value) {
      pl.visibility = visibilityFilter.value;
    }
    if (statusFilter.value) {
      pl.status = statusFilter.value;
    }

    store
      .dispatch('rosterStoreModule/fetchPathwayList', pl)
      .then((response) => {
        const { results, totalResults, totalPages } = response.data;

        callback(results);
        isLoaded.value = true;
        totalPathways.value = totalResults;
        totalPageCount.value = totalPages;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching pathways list-pending',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === 'subscriber') return 'info';
    if (role === 'author') return 'warning';
    if (role === 'maintainer') return 'success';
    if (role === 'editor') return 'info';
    if (role === 'admin') return 'danger';
    return 'info';
  };

  const resolveUserRoleIcon = (role) => {
    if (role === 'subscriber') return 'PathwayIcon';
    if (role === 'author') return 'SettingsIcon';
    if (role === 'maintainer') return 'DatabaseIcon';
    if (role === 'editor') return 'Edit2Icon';
    if (role === 'admin') return 'ServerIcon';
    return 'PathwayIcon';
  };

  const resolvePathwayVisibilityVariant = (visibility) => {
    if (visibility === 'internal') return 'warning';
    if (visibility === 'public') return 'info';
    return 'info';
  };

  const resolvePathwayVisibilityIcon = (visibility) => {
    if (visibility === 'internal') return 'EyeOffIcon';
    if (visibility === 'public') return 'EyeIcon';
    return '';
  };

  const resolvePathwayStatusVariant = (status) => {
    if (status === 'created') return 'secondary';
    if (status === 'pending') return 'warning';
    if (status === 'live') return 'success';
    if (status === 'retired') return 'danger';
    return 'info';
  };

  const resolvePathwayStatusIcon = (status) => {
    if (status === 'created') return '';
    if (status === 'pending') return 'ClockIcon';
    if (status === 'live') return 'GlobeIcon';
    if (status === 'retired') return 'TrashIcon';
    return '';
  };

  const statusOptions = [
    { label: 'Created', value: 'created' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Live', value: 'live' },
    { label: 'Retired', value: 'retired' },
    { label: 'Archived', value: 'archived' },
  ];

  const visibilityOptions = [
    { label: 'Internal', value: 'internal' },
    { label: 'Public', value: 'public' },
  ];

  return {
    fetchPathwayList,
    tableColumns,
    perPage,
    currentPage,
    totalPathways,
    totalPageCount,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refPathwayListTable,
    isLoaded,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolvePathwayVisibilityVariant,
    resolvePathwayVisibilityIcon,
    resolvePathwayStatusIcon,
    resolvePathwayStatusVariant,
    refetchData,

    // Extra Filters
    visibilityFilter,
    statusFilter,

    // Options
    statusOptions,
    visibilityOptions,
  };
}
